import { useState, useEffect } from "react";
import { employeeApi, businessHoursApi } from "../lib/api";
import api from "../lib/api";
import { EMPLOYEE_COLORS, getColorById, getCurrencySymbol, cn } from "../lib/utils";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Checkbox } from "../components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Users, Clock, DollarSign, LayoutGrid, List, X, Lock, UserPlus, Check, Sparkles, Loader2, Upload, Copy, Hash, Send, Shield, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { Badge } from "../components/ui/badge";

const STORAGE_KEY = "employees-view-preference";
const DEFAULT_VIEW = "table"; // Default to table view

// Role configuration with labels and descriptions
const ROLE_CONFIG = {
  staff: {
    label: "Staff",
    description: "View-only access to staff portal",
    icon: Users,
    color: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300"
  },
  manager: {
    label: "Manager",
    description: "Can manage shifts and employees",
    icon: Shield,
    color: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300"
  },
  accounts: {
    label: "Accounts",
    description: "Can manage pay rates and view costs",
    icon: DollarSign,
    color: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300"
  },
  super_admin: {
    label: "Admin",
    description: "Full access to all features",
    icon: ShieldCheck,
    color: "bg-violet-100 text-violet-700 dark:bg-violet-900 dark:text-violet-300"
  }
};

// Copy to clipboard helper component
const CopyButton = ({ text, label = "Copied!" }) => {
  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(text);
      toast.success(label);
    } catch (err) {
      toast.error("Failed to copy");
    }
  };
  
  return (
    <Button
      variant="ghost"
      size="icon"
      className="h-6 w-6 hover:bg-muted"
      onClick={handleCopy}
      title="Copy to clipboard"
    >
      <Copy className="w-3 h-3" />
    </Button>
  );
};

// Staff pack add-ons - matching backend package IDs
const STAFF_PACKS = [
  { id: 'staff_pack_5', name: '5 Extra Staff', employees: 5, price: 10.00 },
  { id: 'staff_pack_10', name: '10 Extra Staff', employees: 10, price: 18.00 },
  { id: 'staff_pack_25', name: '25 Extra Staff', employees: 25, price: 40.00 },
];

const Employees = () => {
  const { subscriptionTier, company, subscription, hasFeatureAccess, isCompanyAdmin } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [deleteEmployee, setDeleteEmployee] = useState(null);
  const [currency, setCurrency] = useState("GBP");
  const [staffPackDialogOpen, setStaffPackDialogOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(null); // track which package is loading
  const [viewMode, setViewMode] = useState(() => {
    // Initialize from localStorage, default to table
    if (typeof window !== 'undefined') {
      return localStorage.getItem(STORAGE_KEY) || DEFAULT_VIEW;
    }
    return DEFAULT_VIEW;
  });
  
  // Roles state (for role assignment to employees)
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  // Handler for purchasing staff packs or upgrades
  const handlePurchase = async (packageId) => {
    setCheckoutLoading(packageId);
    try {
      const response = await api.post('/checkout/create-session', {
        package_id: packageId,
        origin_url: window.location.origin
      });
      
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        toast.error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start checkout process');
    } finally {
      setCheckoutLoading(null);
    }
  };
  
  // Form state
  const [name, setName] = useState("");
  const [role, setRole] = useState("");
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [color, setColor] = useState("coral");
  const [customColor, setCustomColor] = useState("#6366f1");
  const [useCustomColor, setUseCustomColor] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [avatarFile, setAvatarFile] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [payType, setPayType] = useState("hourly"); // "hourly" or "salary"
  const [hourlyRate, setHourlyRate] = useState(0);
  const [additionalRates, setAdditionalRates] = useState([]); // [{name, rate}]
  const [salaryAmount, setSalaryAmount] = useState(0);
  const [salaryPeriod, setSalaryPeriod] = useState("annual"); // "weekly", "monthly", "annual"
  const [hasContractedHours, setHasContractedHours] = useState(false);
  const [minHours, setMinHours] = useState(0);
  const [accessRole, setAccessRole] = useState("staff"); // Access level role (staff, manager, accounts, etc.)
  
  // Invite dialog state
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmployee, setInviteEmployee] = useState(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [sendingInvite, setSendingInvite] = useState(false);
  
  // Role management dialog state
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [roleEmployee, setRoleEmployee] = useState(null);
  const [selectedRole, setSelectedRole] = useState("staff");
  const [updatingRole, setUpdatingRole] = useState(false);

  // Check if user has wage cost feature access using dynamic flags
  const currentTier = subscriptionTier || 'light';
  const hasWageCosts = hasFeatureAccess('wageCostCalculations');
  
  // Get employee limit from subscription data
  const maxEmployees = subscription?.max_employees === "unlimited" 
    ? Infinity 
    : (subscription?.max_employees || 10);
  const isAtEmployeeLimit = maxEmployees !== Infinity && employees.length >= maxEmployees;
  const remainingSlots = maxEmployees === Infinity ? Infinity : Math.max(0, maxEmployees - employees.length);

  // Handle view mode change with persistence
  const handleViewChange = (newView) => {
    setViewMode(newView);
    localStorage.setItem(STORAGE_KEY, newView);
  };

  // Fetch employees
  const fetchEmployees = async () => {
    try {
      setLoading(true);
      const [empRes, hoursRes] = await Promise.all([
        employeeApi.getAll(),
        businessHoursApi.get()
      ]);
      setEmployees(empRes.data);
      setCurrency(hoursRes.data.currency || "GBP");
    } catch (error) {
      console.error("Error fetching employees:", error);
      toast.error("Failed to load employees");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  // Open dialog for create/edit
  const openDialog = (employee = null) => {
    if (employee) {
      setEditingEmployee(employee);
      setName(employee.name);
      setRole(employee.role);
      setEmployeeEmail(employee.email || "");
      // Handle custom color
      if (employee.color && employee.color.startsWith('#')) {
        setUseCustomColor(true);
        setCustomColor(employee.color);
        setColor("coral"); // Reset preset
      } else {
        setUseCustomColor(false);
        setColor(employee.color || "coral");
      }
      setAvatarUrl(employee.avatar_url || "");
      setAvatarFile(null);
      setPayType(employee.pay_type || "hourly");
      setHourlyRate(employee.hourly_rate || 0);
      setAdditionalRates(employee.additional_rates || []);
      setSalaryAmount(employee.salary_amount || 0);
      setSalaryPeriod(employee.salary_period || "annual");
      setHasContractedHours(employee.has_contracted_hours || false);
      setMinHours(employee.min_hours || 0);
      setAccessRole(employee.user_role || "staff");
    } else {
      setEditingEmployee(null);
      setName("");
      setRole("");
      setEmployeeEmail("");
      setColor("coral");
      setUseCustomColor(false);
      setCustomColor("#6366f1");
      setAvatarUrl("");
      setAvatarFile(null);
      setPayType("hourly");
      setHourlyRate(0);
      setAdditionalRates([]);
      setSalaryAmount(0);
      setSalaryPeriod("annual");
      setHasContractedHours(false);
      setMinHours(0);
      setAccessRole("staff");
    }
    setIsDialogOpen(true);
  };

  // Fetch roles
  const fetchRoles = async () => {
    setLoadingRoles(true);
    try {
      const response = await api.get('/roles');
      setRoles(response.data.roles || []);
    } catch (error) {
      console.error('Error fetching roles:', error);
      toast.error('Failed to load roles');
    } finally {
      setLoadingRoles(false);
    }
  };

  // Effect to load roles on initial load (for role assignment dropdown)
  useEffect(() => {
    if (isCompanyAdmin) {
      fetchRoles();
    }
  }, [isCompanyAdmin]);

  // Get role name from ID (for display)
  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (role) return role.name;
    return ROLE_CONFIG[roleId]?.label || roleId?.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Staff';
  };

  // Add a new rate
  const addRate = () => {
    setAdditionalRates(prev => [...prev, { name: "", rate: 0 }]);
  };

  // Update a rate
  const updateRate = (index, field, value) => {
    setAdditionalRates(prev => prev.map((r, i) => 
      i === index ? { ...r, [field]: field === 'rate' ? parseFloat(value) || 0 : value } : r
    ));
  };

  // Remove a rate
  const removeRate = (index) => {
    setAdditionalRates(prev => prev.filter((_, i) => i !== index));
  };

  // Handle avatar file selection
  const handleAvatarSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image must be less than 5MB");
        return;
      }
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      setAvatarFile(file);
      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      setAvatarUrl(previewUrl);
    }
  };

  // Upload avatar to server
  const uploadAvatar = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const res = await api.post('/upload/avatar', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return res.data.url;
    } catch (error) {
      console.error("Error uploading avatar:", error);
      throw error;
    }
  };

  // Handle save
  const handleSave = async () => {
    if (!name.trim() || !role.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    // Filter out empty rate names (only for hourly employees)
    const validRates = payType === "hourly" ? additionalRates.filter(r => r.name.trim()) : [];

    // Determine final color value
    const finalColor = useCustomColor ? customColor : color;

    // Upload avatar if new file selected
    let finalAvatarUrl = avatarUrl;
    if (avatarFile) {
      try {
        setUploadingAvatar(true);
        finalAvatarUrl = await uploadAvatar(avatarFile);
      } catch (error) {
        toast.error("Failed to upload image");
        setUploadingAvatar(false);
        return;
      }
      setUploadingAvatar(false);
    }

    const employeeData = { 
      name, 
      role,
      email: employeeEmail || null,
      color: finalColor,
      avatar_url: finalAvatarUrl || null,
      pay_type: payType,
      hourly_rate: payType === "hourly" ? (parseFloat(hourlyRate) || 0) : 0,
      additional_rates: validRates,
      salary_amount: payType === "salary" ? (parseFloat(salaryAmount) || 0) : 0,
      salary_period: payType === "salary" ? salaryPeriod : "annual",
      has_contracted_hours: hasContractedHours,
      min_hours: hasContractedHours ? parseFloat(minHours) || 0 : 0,
      user_role: accessRole
    };

    try {
      if (editingEmployee) {
        const res = await employeeApi.update(editingEmployee.id, employeeData);
        setEmployees(prev => prev.map(e => e.id === editingEmployee.id ? res.data : e));
        toast.success("Employee updated");
      } else {
        const res = await employeeApi.create(employeeData);
        setEmployees(prev => [...prev, res.data]);
        toast.success("Employee added");
      }
      setIsDialogOpen(false);
    } catch (error) {
      console.error("Error saving employee:", error);
      toast.error("Failed to save employee");
    }
  };

  // Handle invite staff
  const handleInviteClick = (employee) => {
    setInviteEmployee(employee);
    setInviteEmail(employee.email || "");
    setInviteDialogOpen(true);
  };

  const handleSendInvite = async () => {
    if (!inviteEmployee) return;
    
    if (!inviteEmail || !inviteEmail.includes('@')) {
      toast.error("Please enter a valid email address");
      return;
    }
    
    setSendingInvite(true);
    try {
      const response = await api.post(`/employees/${inviteEmployee.id}/invite`, {
        employee_id: inviteEmployee.id,
        email: inviteEmail
      });
      toast.success(response.data.message || "Invite sent!");
      
      // Update employee in list with new email
      if (inviteEmail !== inviteEmployee.email) {
        setEmployees(prev => prev.map(e => 
          e.id === inviteEmployee.id ? { ...e, email: inviteEmail } : e
        ));
      }
      
      setInviteDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invite");
    } finally {
      setSendingInvite(false);
    }
  };

  // Handle role management
  const handleRoleClick = (employee) => {
    setRoleEmployee(employee);
    setSelectedRole(employee.user_role || "staff");
    // Fetch roles if not already loaded
    if (roles.length === 0) {
      fetchRoles();
    }
    setRoleDialogOpen(true);
  };

  const handleUpdateRole = async () => {
    if (!roleEmployee) return;
    
    setUpdatingRole(true);
    try {
      await api.put(`/employees/${roleEmployee.id}/role`, {
        user_role: selectedRole
      });
      
      // Update employee in list
      setEmployees(prev => prev.map(e => 
        e.id === roleEmployee.id ? { ...e, user_role: selectedRole } : e
      ));
      
      toast.success(`Role updated to ${ROLE_CONFIG[selectedRole]?.label || selectedRole}`);
      setRoleDialogOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setUpdatingRole(false);
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!deleteEmployee) return;

    try {
      await employeeApi.delete(deleteEmployee.id);
      setEmployees(prev => prev.filter(e => e.id !== deleteEmployee.id));
      toast.success("Employee deleted");
      setDeleteEmployee(null);
    } catch (error) {
      console.error("Error deleting employee:", error);
      toast.error("Failed to delete employee");
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Team</h2>
          <p className="text-muted-foreground">Manage employees and access levels</p>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <Tabs value={viewMode} onValueChange={handleViewChange}>
            <TabsList>
              <TabsTrigger value="cards" data-testid="view-cards-btn">
                <LayoutGrid className="w-4 h-4 mr-2" />
                Cards
              </TabsTrigger>
              <TabsTrigger value="table" data-testid="view-table-btn">
                <List className="w-4 h-4 mr-2" />
                Table
              </TabsTrigger>
            </TabsList>
          </Tabs>
          {isAtEmployeeLimit ? (
            <Button onClick={() => setStaffPackDialogOpen(true)} data-testid="add-employee-btn" variant="outline">
              <Lock className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          ) : (
            <Button onClick={() => openDialog()} data-testid="add-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
              {remainingSlots <= 3 && remainingSlots > 0 && (
                <span className="ml-2 text-xs bg-primary-foreground/20 px-1.5 py-0.5 rounded">
                  {remainingSlots} left
                </span>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Employee List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : employees.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="w-12 h-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-medium mb-2">No employees yet</h3>
            <p className="text-muted-foreground mb-4">Add your first team member to get started</p>
            <Button onClick={() => openDialog()} data-testid="add-first-employee-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Employee
            </Button>
          </CardContent>
        </Card>
      ) : viewMode === "cards" ? (
        /* Card View */
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="employees-card-view">
          {employees.map((employee) => {
            const colorData = getColorById(employee.color);
            const currencySymbol = getCurrencySymbol(currency);
            const isCustomColor = employee.color?.startsWith('#');
            
            return (
              <Card key={employee.id} className="group relative" data-testid={`employee-card-${employee.id}`}>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    {employee.avatar_url ? (
                      <img 
                        src={employee.avatar_url} 
                        alt={employee.name}
                        className="w-14 h-14 rounded-full object-cover flex-shrink-0 border-2 border-border"
                      />
                    ) : (
                      <div 
                        className={cn(
                          "w-14 h-14 rounded-full flex items-center justify-center font-bold text-xl flex-shrink-0",
                          !isCustomColor && colorData.avatar
                        )}
                        style={isCustomColor ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
                      >
                        {employee.name.charAt(0).toUpperCase()}
                      </div>
                    )}
                    
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold truncate">{employee.name}</h3>
                      <p className="text-sm text-muted-foreground truncate">{employee.role}</p>
                      
                      {/* Employee ID */}
                      {employee.employee_number && (
                        <div className="flex items-center gap-1 mt-1">
                          <Badge variant="outline" className="text-xs font-mono px-1.5 py-0 h-5">
                            <Hash className="w-3 h-3 mr-0.5" />
                            {employee.employee_number}
                          </Badge>
                          <CopyButton text={employee.employee_number} label="Employee ID copied!" />
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 mt-2">
                        <div 
                          className={cn("w-3 h-3 rounded-full", !isCustomColor && colorData.bar)} 
                          style={isCustomColor ? { backgroundColor: employee.color } : {}}
                        />
                        <span className="text-xs text-muted-foreground capitalize">
                          {isCustomColor ? "Custom" : employee.color}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Cost & Hours Info */}
                  <div className="mt-4 pt-4 border-t border-border space-y-2">
                    {hasWageCosts && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">
                          {employee.pay_type === "salary" ? "Salary" : "Rate"}
                        </span>
                        <span className="tabular-nums font-medium">
                          {employee.pay_type === "salary" ? (
                            <>
                              {currencySymbol}{(employee.salary_amount || 0).toLocaleString()}
                              <span className="text-muted-foreground text-xs ml-1">
                                /{employee.salary_period === "weekly" ? "wk" : employee.salary_period === "monthly" ? "mo" : "yr"}
                              </span>
                            </>
                          ) : (
                            <>{currencySymbol}{(employee.hourly_rate || 0).toFixed(2)}/hr</>
                          )}
                        </span>
                      </div>
                    )}
                    {employee.has_contracted_hours && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground flex items-center gap-1">
                          <Clock className="w-3 h-3" /> Min Hours
                        </span>
                        <span className="tabular-nums font-medium">
                          {employee.min_hours || 0}h/week
                        </span>
                      </div>
                    )}
                  </div>

                  {/* Staff Account Status Indicator */}
                  <div className="mt-3 pt-3 border-t">
                    <div className="flex items-center justify-between">
                      {employee.has_login_access ? (
                        <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
                          <Check className="w-3.5 h-3.5" />
                          <span>Staff account active</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleInviteClick(employee)}
                          className="flex items-center gap-2 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors cursor-pointer"
                          data-testid={`status-invite-${employee.id}`}
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span className="underline underline-offset-2">Not yet invited</span>
                        </button>
                      )}
                      {/* Role Badge - only show for staff with accounts */}
                      {employee.has_login_access && employee.user_role && employee.user_role !== 'staff' && (
                        <button
                          onClick={() => handleRoleClick(employee)}
                          className={cn(
                            "px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity",
                            ROLE_CONFIG[employee.user_role]?.color || "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                          )}
                          data-testid={`role-badge-${employee.id}`}
                        >
                          {getRoleName(employee.user_role)}
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
                    {/* Invite button - only show if not already set up */}
                    {!employee.has_login_access && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        onClick={() => handleInviteClick(employee)}
                        title="Invite to Staff Portal"
                        data-testid={`invite-employee-${employee.id}`}
                      >
                        <Send className="w-4 h-4" />
                      </Button>
                    )}
                    {/* Role management button - only show if they have an account */}
                    {employee.has_login_access && (
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                        onClick={() => handleRoleClick(employee)}
                        title="Manage Role"
                        data-testid={`manage-role-${employee.id}`}
                      >
                        <Shield className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openDialog(employee)}
                      data-testid={`edit-employee-${employee.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteEmployee(employee)}
                      data-testid={`delete-employee-${employee.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        /* Table View */
        <Card data-testid="employees-table-view">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Employee ID</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Staff Account</TableHead>
                <TableHead>Access Level</TableHead>
                {hasWageCosts && <TableHead className="text-right">Pay Rate</TableHead>}
                <TableHead className="text-right">Min Hours</TableHead>
                <TableHead className="text-right w-32">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {employees.map((employee) => {
                const colorData = getColorById(employee.color);
                const currencySymbol = getCurrencySymbol(currency);
                const isCustomColor = employee.color?.startsWith('#');
                
                return (
                  <TableRow key={employee.id} data-testid={`employee-row-${employee.id}`}>
                    <TableCell>
                      {employee.avatar_url ? (
                        <img 
                          src={employee.avatar_url} 
                          alt={employee.name}
                          className="w-10 h-10 rounded-full object-cover border border-border"
                        />
                      ) : (
                        <div 
                          className={cn(
                            "w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm",
                            !isCustomColor && colorData.avatar
                          )}
                          style={isCustomColor ? { backgroundColor: employee.color + '20', color: employee.color } : {}}
                        >
                          {employee.name.charAt(0).toUpperCase()}
                        </div>
                      )}
                    </TableCell>
                    <TableCell className="font-medium">{employee.name}</TableCell>
                    <TableCell>
                      {employee.employee_number ? (
                        <div className="flex items-center gap-1">
                          <code className="text-xs bg-muted px-1.5 py-0.5 rounded font-mono">
                            {employee.employee_number}
                          </code>
                          <CopyButton text={employee.employee_number} label="Employee ID copied!" />
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      )}
                    </TableCell>
                    <TableCell className="text-muted-foreground">{employee.role}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div 
                          className={cn("w-3 h-3 rounded-full", !isCustomColor && colorData.bar)} 
                          style={isCustomColor ? { backgroundColor: employee.color } : {}}
                        />
                        <span className="text-sm capitalize">{isCustomColor ? "Custom" : employee.color}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      {employee.has_login_access ? (
                        <div className="flex items-center gap-1.5 text-xs text-green-600 dark:text-green-400">
                          <Check className="w-3.5 h-3.5" />
                          <span>Active</span>
                        </div>
                      ) : (
                        <button
                          onClick={() => handleInviteClick(employee)}
                          className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors cursor-pointer"
                          data-testid={`status-invite-table-${employee.id}`}
                        >
                          <UserPlus className="w-3.5 h-3.5" />
                          <span className="underline underline-offset-2">Not invited</span>
                        </button>
                      )}
                    </TableCell>
                    <TableCell>
                      {employee.has_login_access ? (
                        <button
                          onClick={() => handleRoleClick(employee)}
                          className={cn(
                            "px-2 py-0.5 rounded-full text-xs font-medium cursor-pointer hover:opacity-80 transition-opacity",
                            ROLE_CONFIG[employee.user_role || 'staff']?.color || "bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300"
                          )}
                          data-testid={`role-badge-table-${employee.id}`}
                        >
                          {getRoleName(employee.user_role || 'staff')}
                        </button>
                      ) : (
                        <span className="text-xs text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    {hasWageCosts && (
                      <TableCell className="text-right tabular-nums">
                        {employee.pay_type === "salary" ? (
                          <>
                            {currencySymbol}{(employee.salary_amount || 0).toLocaleString()}
                            <span className="text-muted-foreground text-xs ml-1">
                              /{employee.salary_period === "weekly" ? "wk" : employee.salary_period === "monthly" ? "mo" : "yr"}
                            </span>
                          </>
                        ) : (
                          <>{currencySymbol}{(employee.hourly_rate || 0).toFixed(2)}/hr</>
                        )}
                      </TableCell>
                    )}
                    <TableCell className="text-right tabular-nums">
                      {employee.has_contracted_hours ? `${employee.min_hours || 0}h/week` : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        {/* Invite button - only show if not already set up */}
                        {!employee.has_login_access && (
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-violet-600 hover:text-violet-700 hover:bg-violet-50"
                            onClick={() => handleInviteClick(employee)}
                            title="Invite to Staff Portal"
                            data-testid={`invite-employee-table-${employee.id}`}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openDialog(employee)}
                          data-testid={`edit-employee-table-${employee.id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive hover:text-destructive"
                          onClick={() => setDeleteEmployee(employee)}
                          data-testid={`delete-employee-table-${employee.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent data-testid="employee-dialog" className="max-w-md max-h-[90vh] flex flex-col p-0 gap-0 overflow-hidden">
          <DialogHeader className="flex-shrink-0 px-6 pt-6 pb-4">
            <DialogTitle>
              {editingEmployee ? "Edit Employee" : "Add Employee"}
            </DialogTitle>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto overflow-x-hidden px-6">
            <div className="space-y-4 pb-4">
              {/* Avatar Upload */}
              <div className="space-y-2">
                <Label>Profile Picture</Label>
                <div className="flex items-center gap-4">
                  <div className="relative">
                    {avatarUrl ? (
                      <img 
                        src={avatarUrl} 
                        alt="Avatar preview" 
                        className="w-16 h-16 rounded-full object-cover border-2 border-border"
                      />
                    ) : (
                      <div className={cn(
                        "w-16 h-16 rounded-full flex items-center justify-center text-xl font-semibold border-2 border-border",
                        useCustomColor ? "bg-gray-100 dark:bg-gray-800" : getColorById(color).avatar
                      )}
                      style={useCustomColor ? { backgroundColor: customColor + '20', color: customColor } : {}}
                      >
                        {name ? name.charAt(0).toUpperCase() : "?"}
                      </div>
                    )}
                    {uploadingAvatar && (
                      <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                        <Loader2 className="w-6 h-6 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      type="file"
                      id="avatar-upload"
                      accept="image/*"
                      onChange={handleAvatarSelect}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('avatar-upload').click()}
                      className="w-full"
                    >
                      <Upload className="w-4 h-4 mr-2" />
                      {avatarUrl ? "Change Photo" : "Upload Photo"}
                    </Button>
                    {avatarUrl && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => { setAvatarUrl(""); setAvatarFile(null); }}
                        className="w-full text-destructive hover:text-destructive"
                      >
                        <X className="w-4 h-4 mr-2" />
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter employee name"
                  data-testid="employee-name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="role">Role</Label>
                <Input
                  id="role"
                  value={role}
                  onChange={(e) => setRole(e.target.value)}
                  placeholder="Enter role or position"
                  data-testid="employee-role-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="employeeEmail">Email Address (optional)</Label>
                <Input
                  id="employeeEmail"
                  type="email"
                  value={employeeEmail}
                  onChange={(e) => setEmployeeEmail(e.target.value)}
                  placeholder="employee@company.com"
                  data-testid="employee-email-input"
                />
                <p className="text-xs text-muted-foreground">Used for staff invitations and notifications</p>
              </div>

              {/* Access Level */}
              <div className="space-y-2">
                <Label htmlFor="accessRole">Access Level</Label>
                <Select value={accessRole} onValueChange={setAccessRole}>
                  <SelectTrigger data-testid="employee-access-role-select">
                    <SelectValue placeholder="Select access level" />
                  </SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => (
                      <SelectItem key={r.id} value={r.id}>
                        {r.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Determines what the employee can access when logged in
                </p>
              </div>

              <div className="space-y-2">
                <Label>Color</Label>
                <Select 
                  value={useCustomColor ? "custom" : color} 
                  onValueChange={(val) => {
                    if (val === "custom") {
                      setUseCustomColor(true);
                    } else {
                      setUseCustomColor(false);
                      setColor(val);
                    }
                  }}
                >
                  <SelectTrigger data-testid="employee-color-select">
                    <SelectValue placeholder="Select a color" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYEE_COLORS.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        <div className="flex items-center gap-2">
                          <div className={cn("w-4 h-4 rounded-full", c.bar)} />
                          {c.name}
                        </div>
                      </SelectItem>
                    ))}
                    <SelectItem value="custom">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-4 h-4 rounded-full border border-dashed border-muted-foreground/50"
                          style={{ backgroundColor: customColor }}
                        />
                        Custom Color...
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                {/* Custom Color Picker (shown when custom selected) */}
                {useCustomColor && (
                  <div className="flex items-center gap-2 mt-2 p-2 rounded-lg border bg-muted/30">
                    <input
                      type="color"
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      className="w-8 h-8 rounded cursor-pointer border-0"
                    />
                    <Input
                      value={customColor}
                      onChange={(e) => setCustomColor(e.target.value)}
                      placeholder="#000000"
                      className="w-28 uppercase"
                    />
                  </div>
                )}
              </div>

              {/* Pay Type Toggle - Locked for Light plan */}
              {hasWageCosts ? (
                <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
                <Label className="font-medium">Pay Type</Label>
                <Tabs value={payType} onValueChange={setPayType} className="w-full">
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="hourly" data-testid="pay-type-hourly">Hourly</TabsTrigger>
                    <TabsTrigger value="salary" data-testid="pay-type-salary">Salary</TabsTrigger>
                  </TabsList>
                </Tabs>

                {payType === "hourly" ? (
                  <div className="space-y-4 pt-2">
                    {/* Hourly Rate */}
                    <div className="space-y-2">
                      <Label htmlFor="hourlyRate">Default Hourly Rate ({getCurrencySymbol(currency)})</Label>
                      <Input
                        id="hourlyRate"
                        type="number"
                        min="0"
                        step="0.01"
                        value={hourlyRate}
                        onChange={(e) => setHourlyRate(e.target.value)}
                        onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setHourlyRate(''); }}
                        placeholder="0.00"
                        data-testid="employee-hourly-rate-input"
                      />
                    </div>

                    {/* Additional Pay Rates */}
                    <div className="space-y-2 pt-2 border-t">
                      <div className="flex items-center justify-between pt-2">
                        <Label className="text-sm">Additional Pay Rates</Label>
                        <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={addRate}
                        className="h-7 text-xs"
                        data-testid="add-rate-btn"
                      >
                        <Plus className="w-3 h-3 mr-1" />
                        Add Rate
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Add alternative rates like overtime, weekend, or night shift pay
                    </p>
                    {additionalRates.length > 0 && (
                      <div className="space-y-2 pt-2">
                        {additionalRates.map((rate, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <Input
                              placeholder="Rate name (e.g., Overtime)"
                              value={rate.name}
                              onChange={(e) => updateRate(index, 'name', e.target.value)}
                              className="flex-1 h-8 text-sm"
                            />
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-muted-foreground">{getCurrencySymbol(currency)}</span>
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="0.00"
                                value={rate.rate}
                                onChange={(e) => updateRate(index, 'rate', e.target.value)}
                                className="w-20 h-8 text-sm tabular-nums"
                              />
                              <span className="text-xs text-muted-foreground">/hr</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeRate(index)}
                              className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className="space-y-4 pt-2">
                  {/* Salary Period */}
                  <div className="space-y-2">
                    <Label>Salary Period</Label>
                    <Select value={salaryPeriod} onValueChange={setSalaryPeriod}>
                      <SelectTrigger data-testid="salary-period-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="annual">Annual</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Salary Amount */}
                  <div className="space-y-2">
                    <Label htmlFor="salaryAmount">
                      {salaryPeriod === "weekly" ? "Weekly" : salaryPeriod === "monthly" ? "Monthly" : "Annual"} Salary ({getCurrencySymbol(currency)})
                    </Label>
                    <Input
                      id="salaryAmount"
                      type="number"
                      min="0"
                      step="0.01"
                      value={salaryAmount}
                      onChange={(e) => setSalaryAmount(e.target.value)}
                      onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setSalaryAmount(''); }}
                      placeholder="0.00"
                      data-testid="employee-salary-amount-input"
                    />
                    {salaryAmount && parseFloat(salaryAmount) >= 1000 && (
                      <p className="text-xs text-muted-foreground">
                        {getCurrencySymbol(currency)}{parseFloat(salaryAmount).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </p>
                    )}
                  </div>

                  <p className="text-xs text-muted-foreground">
                    Salary employees have a fixed cost regardless of hours worked. Split pay rates are not available for salary-based employees.
                  </p>
                </div>
              )}
              </div>
              ) : (
                /* Locked Pay Type section for Light plan */
                <div className="relative p-4 rounded-lg border bg-muted/30">
                  <div className="opacity-50 pointer-events-none">
                    <Label className="font-medium">Pay Type</Label>
                    <div className="mt-3 grid w-full grid-cols-2 rounded-lg bg-muted p-1">
                      <div className="rounded-md bg-background px-3 py-1.5 text-sm text-center shadow-sm">Hourly</div>
                      <div className="px-3 py-1.5 text-sm text-center text-muted-foreground">Salary</div>
                    </div>
                    <div className="mt-3 space-y-2">
                      <Label className="text-sm text-muted-foreground">Hourly Rate</Label>
                      <Input disabled placeholder="0.00" className="bg-muted" />
                    </div>
                  </div>
                  <div className="absolute inset-0 flex items-center justify-center bg-background/60 rounded-lg">
                    <div className="flex flex-col items-center gap-2 text-center px-4">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                      </div>
                      <p className="text-sm font-medium">Wage Cost Tracking</p>
                      <p className="text-xs text-muted-foreground">Upgrade to Standard to track hourly rates and salary costs</p>
                      <Button size="sm" className="mt-1 h-7 text-xs bg-blue-600 hover:bg-blue-700">
                        Upgrade to Standard
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Contracted Hours */}
            <div className="space-y-3 p-4 rounded-lg border bg-muted/30">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="hasContractedHours"
                  checked={hasContractedHours}
                  onCheckedChange={setHasContractedHours}
                  data-testid="employee-contracted-hours-checkbox"
                />
                <Label htmlFor="hasContractedHours" className="font-medium cursor-pointer">
                  Has Contracted Hours
                </Label>
              </div>
              {hasContractedHours && (
                <div className="space-y-2 pt-2">
                  <Label htmlFor="minHours">Minimum Hours per Week</Label>
                  <Input
                    id="minHours"
                    type="number"
                    min="0"
                    step="0.5"
                    value={minHours}
                    onChange={(e) => setMinHours(e.target.value)}
                    onFocus={(e) => { if (e.target.value === '0' || e.target.value === 0) setMinHours(''); }}
                    placeholder="0"
                    data-testid="employee-min-hours-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    A warning will show in Week View if scheduled hours fall below this minimum
                  </p>
                </div>
              )}
            </div>

            {/* Preview */}
            <div className="p-4 rounded-lg bg-muted/50">
              <p className="text-sm text-muted-foreground mb-2">Preview</p>
              <div className="flex items-center gap-3">
                <div className={cn(
                  "w-12 h-12 rounded-full flex items-center justify-center font-bold",
                  getColorById(color).avatar
                )}>
                  {name ? name.charAt(0).toUpperCase() : "?"}
                </div>
                <div>
                  <p className="font-medium">{name || "Employee Name"}</p>
                  <p className="text-sm text-muted-foreground">{role || "Role"}</p>
                  {hasWageCosts && hourlyRate > 0 && (
                    <p className="text-xs text-muted-foreground tabular-nums">
                      {getCurrencySymbol(currency)}{parseFloat(hourlyRate).toFixed(2)}/hr
                    </p>
                  )}
                </div>
              </div>
            </div>
            </div>
          </div>

          <DialogFooter className="flex-shrink-0 border-t px-6 py-4">
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} data-testid="employee-save-btn">
              {editingEmployee ? "Update" : "Add"} Employee
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteEmployee} onOpenChange={() => setDeleteEmployee(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Employee?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete {deleteEmployee?.name} and all their scheduled shifts. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-btn">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Staff Pack Add-on Dialog */}
      <Dialog open={staffPackDialogOpen} onOpenChange={setStaffPackDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-6 h-6 text-blue-600 dark:text-blue-400" />
            </div>
            <DialogTitle className="text-center">Add More Team Members</DialogTitle>
            <DialogDescription className="text-center">
              You&apos;ve reached your limit of {maxEmployees} employees. Purchase a staff pack to add more team members.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4">
            {STAFF_PACKS.map((pack) => (
              <Card 
                key={pack.id} 
                className={`cursor-pointer hover:border-blue-500 transition-colors ${checkoutLoading === pack.id ? 'opacity-75' : ''}`}
                onClick={() => !checkoutLoading && handlePurchase(pack.id)}
                data-testid={`staff-pack-${pack.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                        {checkoutLoading === pack.id ? (
                          <Loader2 className="w-5 h-5 text-blue-600 dark:text-blue-400 animate-spin" />
                        ) : (
                          <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                      <div>
                        <p className="font-semibold">{pack.name}</p>
                        <p className="text-sm text-muted-foreground">+{pack.employees} employee slots</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold">£{pack.price.toFixed(2)}</p>
                      <p className="text-xs text-muted-foreground">/month</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="border-t pt-4">
            <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/30 dark:to-indigo-950/30 border-blue-200 dark:border-blue-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Sparkles className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                  <div className="flex-1">
                    <p className="font-semibold text-sm">Upgrade to Professional</p>
                    <p className="text-xs text-muted-foreground">Unlimited employees included</p>
                  </div>
                  <Button 
                    size="sm" 
                    className="bg-blue-600 hover:bg-blue-700"
                    onClick={() => handlePurchase('professional_monthly')}
                    disabled={checkoutLoading === 'professional_monthly'}
                    data-testid="upgrade-to-pro-btn"
                  >
                    {checkoutLoading === 'professional_monthly' ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      '£59/mo'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          <DialogFooter className="sm:justify-center">
            <Button variant="ghost" onClick={() => setStaffPackDialogOpen(false)} disabled={checkoutLoading !== null}>
              Maybe Later
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite Staff Dialog */}
      <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-4">
              <Send className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <DialogTitle className="text-center">Invite Staff Member</DialogTitle>
            <DialogDescription className="text-center">
              Send an invitation to <strong>{inviteEmployee?.name}</strong> to set up their staff portal account.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="inviteEmail">Email Address</Label>
              <Input
                id="inviteEmail"
                type="email"
                placeholder="employee@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
              <p className="text-xs text-muted-foreground">
                We&apos;ll send them an email with their login details and a link to set up their password.
              </p>
            </div>

            {inviteEmployee && (
              <div className="bg-muted/50 rounded-lg p-3 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Employee ID:</span>
                  <code className="bg-background px-2 py-0.5 rounded">{inviteEmployee.employee_number}</code>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Role:</span>
                  <span>{inviteEmployee.role || "Staff"}</span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialogOpen(false)} disabled={sendingInvite}>
              Cancel
            </Button>
            <Button 
              onClick={handleSendInvite} 
              disabled={sendingInvite || !inviteEmail}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="send-invite-btn"
            >
              {sendingInvite ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Sending...</>
              ) : (
                <><Send className="w-4 h-4 mr-2" /> Send Invite</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Role Management Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="w-12 h-12 rounded-full bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center mx-auto mb-4">
              <Shield className="w-6 h-6 text-violet-600 dark:text-violet-400" />
            </div>
            <DialogTitle className="text-center">Manage Access Level</DialogTitle>
            <DialogDescription className="text-center">
              Set the access level for <strong>{roleEmployee?.name}</strong>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
            {/* Default Roles */}
            {Object.entries(ROLE_CONFIG).map(([roleId, roleInfo]) => {
              const RoleIcon = roleInfo.icon;
              const isSelected = selectedRole === roleId;
              
              return (
                <div
                  key={roleId}
                  onClick={() => setSelectedRole(roleId)}
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    isSelected 
                      ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" 
                      : "border-border hover:border-violet-200 dark:hover:border-violet-800"
                  )}
                  data-testid={`role-option-${roleId}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                      roleInfo.color
                    )}>
                      <RoleIcon className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{roleInfo.label}</p>
                        {isSelected && (
                          <Check className="w-4 h-4 text-violet-600" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{roleInfo.description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Custom Roles */}
            {roles.filter(r => !r.is_default).map((customRole) => {
              const isSelected = selectedRole === customRole.id;
              
              return (
                <div
                  key={customRole.id}
                  onClick={() => setSelectedRole(customRole.id)}
                  className={cn(
                    "p-4 rounded-lg border-2 cursor-pointer transition-all",
                    isSelected 
                      ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20" 
                      : "border-border hover:border-violet-200 dark:hover:border-violet-800"
                  )}
                  data-testid={`role-option-${customRole.id}`}
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300">
                      <Sparkles className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold">{customRole.name}</p>
                        <span className="text-xs bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 px-1.5 py-0.5 rounded">Custom</span>
                        {isSelected && (
                          <Check className="w-4 h-4 text-violet-600" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{customRole.description || 'Custom role'}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Permissions Preview */}
          {selectedRole && selectedRole !== 'staff' && (
            <div className="bg-muted/50 rounded-lg p-4 space-y-2">
              <p className="text-sm font-medium">Permissions:</p>
              <div className="grid grid-cols-2 gap-2 text-xs">
                {selectedRole === 'manager' && (
                  <>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Manage employees
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Manage shifts
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <X className="w-3 h-3" /> View wage costs
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <X className="w-3 h-3" /> Billing access
                    </div>
                  </>
                )}
                {selectedRole === 'accounts' && (
                  <>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> View wage costs
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Manage pay rates
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Billing access
                    </div>
                    <div className="flex items-center gap-1.5 text-muted-foreground">
                      <X className="w-3 h-3" /> Manage shifts
                    </div>
                  </>
                )}
                {selectedRole === 'super_admin' && (
                  <>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Full access
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Manage users
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Billing access
                    </div>
                    <div className="flex items-center gap-1.5 text-green-600">
                      <Check className="w-3 h-3" /> Settings
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialogOpen(false)} disabled={updatingRole}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateRole} 
              disabled={updatingRole || selectedRole === roleEmployee?.user_role}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="update-role-btn"
            >
              {updatingRole ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating...</>
              ) : (
                <><Shield className="w-4 h-4 mr-2" /> Update Role</>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Employees;
